import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * The dashboard's card shell (docs/design/dashboard): white, 1px line,
 * uppercase bold title, an optional action ("View all", a period select) on
 * the right. `flex-col` so a card stretched to its row's height can push a
 * footer to the bottom with `mt-auto`.
 */
export function DashCard({ title, action, children, className }: { title: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('flex min-w-0 flex-1 flex-col rounded-[var(--radius)] border border-line bg-card px-[18px] py-4 shadow-card', className)}>
      <div className="flex min-h-[26px] items-center justify-between gap-2">
        <h2 className="m-0 text-[13px] font-bold uppercase tracking-[.02em] text-ink">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function ViewAllButton({ onClick, label = 'View all' }: { onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 rounded-md border border-control-line bg-card px-2.5 py-1 text-[11.5px] font-medium text-teal transition-colors hover:bg-muted/60"
    >
      {label}
    </button>
  );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "15 Aug 2026" from a pg DATE / ISO string — fixed abbreviations (Intl prints "Sept" on newer ICU). */
export function fmtDay(value: string): string {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return value;
  return `${String(d).padStart(2, '0')} ${MONTHS[m - 1]} ${y}`;
}

/** Minutes as "42m" / "1h 5m"; "—" for none. */
export const fmtMin = (m: number | null | undefined) =>
  m === null || m === undefined ? '—' : m < 60 ? `${Math.round(m)}m` : `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`;

/** "Dr. Menon" — without doubling a title already stored with the name ("Dr.Sekar"). */
export const drName = (name: string) => (/^dr\b\.?/i.test(name.trim()) ? name.trim() : `Dr. ${name.trim()}`);
